import React, { useState, useRef, useMemo } from 'react';
import {
  Popover, Box, Button, Typography, IconButton, List, ListItem, ListItemText, Paper
} from '@mui/material';
import { XCircle, Image, CaretDown, CaretUp } from '@phosphor-icons/react';
import { Trash, ArrowClockwise } from '@phosphor-icons/react';
import { useReactFlow } from 'reactflow';
import useFlowsStore from '../../store/storeFlows';
import { useAuthStore } from '../../store/storeAuth';
import { useNotificationStore } from '../../store/storeNotifications';
import { UploadService } from '../../service/UploadService';
import { TinyMCERichTextInput, TinyMCERichTextInputRef } from '../UI/TinyMCERichTextInput';
import { buildReferenceSuggestions } from '../../util/references/referenceTokens';

interface HeaderFooterReportBuilderProps {
  open: boolean;
  anchorEl: HTMLElement | null;
  onClose: () => void;
  nodeId: string;
  onSave?: () => void;
  formData: any;
  onChange: (e: { target: { name: string; value: string } }) => void;
}

export default function HeaderFooterReportBuilder({ 
  open, 
  anchorEl,
  onClose, 
  nodeId,
  onSave,
  formData,
  onChange
}: HeaderFooterReportBuilderProps) {
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [suggestionAnchorEl, setSuggestionAnchorEl] = useState<HTMLElement | null>(null);
  const [activeEditor, setActiveEditor] = useState<string | null>(null);
  
  const [backgroundMenuAnchor, setBackgroundMenuAnchor] = useState<HTMLElement | null>(null);
  const [activeBackgroundSection, setActiveBackgroundSection] = useState<'header' | 'footer' | null>(null);

  const leftHeaderRef = useRef<TinyMCERichTextInputRef>(null);
  const centerHeaderRef = useRef<TinyMCERichTextInputRef>(null);
  const rightHeaderRef = useRef<TinyMCERichTextInputRef>(null);
  const leftFooterRef = useRef<TinyMCERichTextInputRef>(null);
  const centerFooterRef = useRef<TinyMCERichTextInputRef>(null);
  const rightFooterRef = useRef<TinyMCERichTextInputRef>(null);
  
  const schema = useFlowsStore(state => state.schema);
  const { user } = useAuthStore(state => state);
  const { getNodes } = useReactFlow();
  const setNotification = useNotificationStore(state => state.setNotification);

  const referenceSuggestions = useMemo(
    () => buildReferenceSuggestions(getNodes() as any),
    // schema changes whenever node data/labels change, so refresh suggestions
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [schema],
  );
  const displayToValue = useMemo(() => {
    const map: Record<string, string> = {};
    referenceSuggestions.forEach(s => {
      map[s.display] = s.value;
    });
    return map;
  }, [referenceSuggestions]);

  const handleSave = () => {
    if (onSave) {
      onSave();
    }
    onClose();
  };

  const handleVariableSuggestions = (editorId: string) => (
    show: boolean, 
    suggestionList: string[], 
    position: { x: number; y: number },
    anchorElement?: HTMLElement
  ) => {
    setActiveEditor(show ? editorId : null);
    setShowSuggestions(show);
    setSuggestions(suggestionList);
    setSuggestionAnchorEl(anchorElement || null);
  };

  const insertSuggestion = (suggestion: string) => {
    if (!activeEditor) return;
    
    const editorRef = getEditorRef(activeEditor);
    if (editorRef?.current) {
      editorRef.current.insertVariable(displayToValue[suggestion] ?? suggestion);
    }
    
    setShowSuggestions(false);
    setActiveEditor(null);
  };

  const getEditorRef = (fieldId: string) => {
    switch (fieldId) {
      case 'header-left': return leftHeaderRef;
      case 'header-center': return centerHeaderRef;
      case 'header-right': return rightHeaderRef;
      case 'footer-left': return leftFooterRef;
      case 'footer-center': return centerFooterRef;
      case 'footer-right': return rightFooterRef;
      default: return null;
    }
  };


  const getVariablePaths = () => {
    return referenceSuggestions.map(s => s.display);
  };

  const handleBackgroundImageUpload = async (section: 'header' | 'footer') => {
    const fileInput = document.createElement('input');
    fileInput.type = 'file';
    fileInput.accept = 'image/*';
    fileInput.style.display = 'none';
    
    fileInput.onchange = async (event: Event) => {
      const target = event.target as HTMLInputElement;
      const file = target.files?.[0];
      
      if (!file) return;
      
      try {
        setNotification({
          type: 'info',
          message: 'Uploading background image...',
        });

        const formData = new FormData();
        formData.append('file', file);
        
        const result = await UploadService.uploadFile(formData);
        
        if (result.data && result.data.src) {
          const fieldName = section === 'header' ? 'headerBackgroundImage' : 'footerBackgroundImage';
          onChange({
            target: {
              name: fieldName,
              value: result.data.src,
            },
          });
          
          setNotification({
            type: 'success',
            message: 'Background image successfully added!',
          });
        }
      } catch (error) {
        console.error('Error uploading background image:', error);
        setNotification({
          type: 'error',
          message: 'Failed to upload background image. Please try again.',
        });
      }
    };
    
    document.body.appendChild(fileInput);
    fileInput.click();
    document.body.removeChild(fileInput);
  };

  const handleBackgroundMenuClick = (event: React.MouseEvent<HTMLElement>, section: 'header' | 'footer') => {
    const hasBackground = section === 'header' ? formData.headerBackgroundImage : formData.footerBackgroundImage;
    
    if (hasBackground) {
      setBackgroundMenuAnchor(event.currentTarget);
      setActiveBackgroundSection(section);
    } else {
      handleBackgroundImageUpload(section);
    }
  };

  const handleReplaceBackground = () => {
    if (activeBackgroundSection) {
      handleBackgroundImageUpload(activeBackgroundSection);
    }
    setBackgroundMenuAnchor(null);
    setActiveBackgroundSection(null);
  };

  const handleRemoveBackground = () => {
    if (activeBackgroundSection) {
      const fieldName = activeBackgroundSection === 'header' ? 'headerBackgroundImage' : 'footerBackgroundImage';
      onChange({
        target: {
          name: fieldName,
          value: '',
        },
      });
    }
    
    setNotification({
      type: 'success',
      message: 'Background image removed!',
    });
    
    setBackgroundMenuAnchor(null);
    setActiveBackgroundSection(null);
  };

  const renderRichTextEditor = (
    value: string,
    fieldName: string,
    fieldId: string,
    ref: React.RefObject<TinyMCERichTextInputRef>
  ) => {
    return (
      <TinyMCERichTextInput
        key={fieldId}
        ref={ref}
        value={value}
        onChange={(content) => onChange({ target: { name: fieldName, value: content } })}
        placeholder="Type here..."
        height={120}
        showToolbar={true}
        fieldId={fieldId}
        suggestions={getVariablePaths()}
        onVariableSuggestions={handleVariableSuggestions(fieldId)}
      />
    );
  };

  return (
    <Popover
      open={open}
      anchorEl={null}
      onClose={onClose}
      anchorReference="none"
      sx={{
        '& .MuiPopover-paper': {
          position: 'fixed !important',
          top: '50% !important',
          left: '50% !important',
          transform: 'translate(-50%, -50%) !important',
          margin: 0,
        }
      }}
      PaperProps={{
        sx: {
          width: '90vw',
          maxWidth: '1200px',
          minWidth: '800px',
          border: `1px solid ${user?.theme === 'dark' ? '#475467' : '#EAECF0'}`,
          boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.1)',
          borderRadius: '16px',
        }
      }}
    >
      <Box sx={{ 
        display: 'flex',
        flexDirection: 'column',
        bgcolor: user?.theme === 'dark' ? '#1D2939' : '#FFFFFF',
        position: 'relative',
      }}>
        <Box sx={{ 
          position: 'relative',
          pt: 7,
          px: 2,
          pb: 2,
          bgcolor: user?.theme === 'dark' ? '#1D2939' : '#FFFFFF',
        }}>
          <IconButton
            onClick={onClose}
            size="small"
            sx={{ 
              position: 'absolute',
              top: 8,
              right: 8,
              color: user?.theme === 'dark' ? '#98A2B3' : '#667085',
            }}
          >
            <XCircle size={20} />
          </IconButton>
          <Box>
            <Typography 
              variant="h6" 
              sx={{
                color: user?.theme === 'dark' ? '#D0D5DD' : '#101828',
                fontWeight: 600,
                fontSize: '16px',
                mb: 1,
              }}
            >
              Customize Document
            </Typography>
            <Typography 
              variant="body2"
              sx={{
                color: user?.theme === 'dark' ? '#98A2B3' : '#667085',
                fontSize: '12px',
              }}
            >
              Add formatted headers and footers with variables. Use {"{{"} to insert variables.
            </Typography>
          </Box>
        </Box>

        <Box 
          sx={{ 
            p: 2,
            display: 'flex',
            flexDirection: 'column',
            gap: 3,
            overflow: 'visible',
            position: 'relative',
          }}
          onClick={(e) => {
            const target = e.target as HTMLElement;
            const isInEditor = target.closest('[data-section]');
            const isInSuggestions = target.closest('.suggestions-list');
            
            if (!isInEditor && !isInSuggestions && showSuggestions) {
              setShowSuggestions(false);
              setActiveEditor(null);
            }
          }}
        >
          <Box 
            data-section="header"
            sx={{
              bgcolor: user?.theme === 'dark' ? '#101828' : '#F9FAFB',
              border: `1px solid ${user?.theme === 'dark' ? '#475467' : '#EAECF0'}`,
              borderRadius: '12px',
              padding: '16px',
              position: 'relative',
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography 
                variant="subtitle2"
                sx={{
                  color: user?.theme === 'dark' ? '#D0D5DD' : '#101828',
                  fontWeight: 600,
                  fontSize: '14px',
                }}
              >
                Header
              </Typography>
              
              <IconButton
                size="small"
                onClick={(e) => handleBackgroundMenuClick(e, 'header')}
                sx={{
                  color: user?.theme === 'dark' ? '#98A2B3' : '#667085',
                  '&:hover': {
                    bgcolor: user?.theme === 'dark' ? '#344054' : '#F3F4F6',
                  },
                }}
                title={formData.headerBackgroundImage ? 'Edit Background' : 'Add Background'}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  {formData.headerBackgroundImage ? <Image size={20} weight="fill" /> : <Image size={20} />}
                  {formData.headerBackgroundImage && (
                    activeBackgroundSection === 'header' && Boolean(backgroundMenuAnchor)
                      ? <CaretUp size={12} />
                      : <CaretDown size={12} />
                  )}
                </Box>
              </IconButton>
            </Box>
            
              <Box sx={{
                display: 'grid', 
                gridTemplateColumns: '1fr 1fr 1fr', 
                gap: 0,
                position: 'relative',
                overflow: 'hidden',
                bgcolor: user?.theme === 'dark' ? '#1D2939' : '#FFFFFF',
              }}>
                <Box sx={{ 
                  '& .tox-tinymce': {
                    border: 'none !important',
                    borderRadius: '0 !important',
                  }
                }}>
                  {renderRichTextEditor(formData.leftHeader, 'leftHeader', 'header-left', leftHeaderRef)}
                </Box>
                <Box sx={{ 
                  '& .tox-tinymce': {
                    border: 'none !important',
                    borderRadius: '0 !important',
                  }
                }}>
                  {renderRichTextEditor(formData.centerHeader, 'centerHeader', 'header-center', centerHeaderRef)}
                </Box>
                <Box sx={{ 
                  '& .tox-tinymce': {
                    border: 'none !important',
                    borderRadius: '0 !important',
                  }
                }}>
                  {renderRichTextEditor(formData.rightHeader, 'rightHeader', 'header-right', rightHeaderRef)}
                </Box>
              </Box>
          </Box>

          <Box 
            data-section="footer"
            sx={{
                bgcolor: user?.theme === 'dark' ? '#101828' : '#F9FAFB',
              border: `1px solid ${user?.theme === 'dark' ? '#475467' : '#EAECF0'}`,
              borderRadius: '12px',
              padding: '16px',
              position: 'relative',
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography 
                variant="subtitle2"
                sx={{
                  color: user?.theme === 'dark' ? '#D0D5DD' : '#101828',
                  fontWeight: 600,
                  fontSize: '14px',
                }}
              >
                Footer
              </Typography>
              
              <IconButton
                size="small"
                onClick={(e) => handleBackgroundMenuClick(e, 'footer')}
                sx={{
                  color: user?.theme === 'dark' ? '#98A2B3' : '#667085',
                  '&:hover': {
                    bgcolor: user?.theme === 'dark' ? '#344054' : '#F3F4F6',
                  },
                }}
                title={formData.footerBackgroundImage ? 'Edit Background' : 'Add Background'}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  {formData.footerBackgroundImage ? <Image size={20} weight="fill" /> : <Image size={20} />}
                  {formData.footerBackgroundImage && (
                    activeBackgroundSection === 'footer' && Boolean(backgroundMenuAnchor)
                      ? <CaretUp size={12} />
                      : <CaretDown size={12} />
                  )}
                </Box>
              </IconButton>
            </Box>
            
            <Box sx={{
              display: 'grid', 
              gridTemplateColumns: '1fr 1fr 1fr', 
              gap: 0,
              position: 'relative',
              overflow: 'hidden',
              bgcolor: user?.theme === 'dark' ? '#1D2939' : '#FFFFFF',
            }}>
              <Box sx={{ 
                '& .tox-tinymce': {
                  border: 'none !important',
                  borderRadius: '0 !important',
                }
              }}>
                {renderRichTextEditor(formData.leftFooter, 'leftFooter', 'footer-left', leftFooterRef)}
              </Box>
              <Box sx={{ 
                '& .tox-tinymce': {
                  border: 'none !important',
                  borderRadius: '0 !important',
                }
              }}>
                {renderRichTextEditor(formData.centerFooter, 'centerFooter', 'footer-center', centerFooterRef)}
              </Box>
              <Box sx={{ 
                '& .tox-tinymce': {
                  border: 'none !important',
                  borderRadius: '0 !important',
                }
              }}>
                {renderRichTextEditor(formData.rightFooter, 'rightFooter', 'footer-right', rightFooterRef)}
              </Box>
            </Box>
              </Box>
              
          {showSuggestions && suggestions.length > 0 && (
            <Popover
              open={showSuggestions}
              anchorEl={suggestionAnchorEl}
              onClose={() => {
                setShowSuggestions(false);
                setSuggestionAnchorEl(null);
                setActiveEditor(null);
              }}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'center',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'center',
              }}
              disableAutoFocus
              disableEnforceFocus
              PaperProps={{
                sx: {
                  maxHeight: '200px',
                  minWidth: '200px',
                  bgcolor: user?.theme === 'dark' ? '#1D2939' : '#FFFFFF',
                  border: `1px solid ${user?.theme === 'dark' ? '#475467' : '#EAECF0'}`,
                  borderRadius: '8px',
                  boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.1)',
                }
              }}
            >
              <List dense>
                {suggestions.map((suggestion, index) => (
                  <ListItem
                    key={index}
                    button
                    onClick={() => insertSuggestion(suggestion)}
                    sx={{
                      '&:hover': {
                        bgcolor: user?.theme === 'dark' ? '#344054' : '#F3F4F6',
                      },
                    }}
                  >
                    <ListItemText
                      primary={suggestion}
                      sx={{
                        '& .MuiListItemText-primary': {
                          color: user?.theme === 'dark' ? '#D0D5DD' : '#101828',
                          fontSize: '13px',
                          fontFamily: 'monospace',
                        },
                      }}
                    />
                  </ListItem>
                ))}
              </List>
            </Popover>
          )}

          <Popover
            open={Boolean(backgroundMenuAnchor)}
            anchorEl={backgroundMenuAnchor}
            onClose={() => {
              setBackgroundMenuAnchor(null);
              setActiveBackgroundSection(null);
            }}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            PaperProps={{
              sx: {
                minWidth: '160px',
                bgcolor: user?.theme === 'dark' ? '#1D2939' : '#FFFFFF',
                border: `1px solid ${user?.theme === 'dark' ? '#475467' : '#EAECF0'}`,
                borderRadius: '8px',
                boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.1)',
              }
            }}
          >
            <List dense sx={{ py: 1 }}>
              <ListItem
                button
                onClick={handleReplaceBackground}
                sx={{
                  '&:hover': {
                    bgcolor: user?.theme === 'dark' ? '#344054' : '#F3F4F6',
                  },
                }}
              >
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <span style={{ color: user?.theme === 'dark' ? '#D0D5DD' : '#101828', fontSize: '14px' }}>Replace Background</span>
                      <ArrowClockwise size={16} style={{ marginLeft: 8, color: user?.theme === 'dark' ? '#D0D5DD' : '#101828' }} />
                    </Box>
                  }
                />
              </ListItem>
              <ListItem
                button
                onClick={handleRemoveBackground}
                sx={{
                  '&:hover': {
                    bgcolor: user?.theme === 'dark' ? '#344054' : '#F3F4F6',
                  },
                }}
              >
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <span style={{ color: '#E53935', fontSize: '14px' }}>Remove Background</span>
                      <Trash size={16} style={{ marginLeft: 8, color: '#E53935' }} />
                    </Box>
                  }
                />
              </ListItem>
            </List>
          </Popover>
            </Box>

        <Box sx={{ 
          p: 2,
          display: 'flex', 
          gap: 1, 
          justifyContent: 'flex-end',
          bgcolor: user?.theme === 'dark' ? '#1D2939' : '#FFFFFF',
        }}>
          <Button 
            size="small"
            variant="contained" 
            onClick={handleSave}
            sx={{
              bgcolor: '#3650DE',
              fontSize: '12px',
              '&:hover': {
                bgcolor: '#2940C7',
              },
            }}
          >
            Save Changes
          </Button>
        </Box>
      </Box>
    </Popover>
  );
} 